import React from "react";
import {Box, styled, Typography} from "@mui/material";
import BackGo from "@/components/back-go/BackGo";
import {useTranslation} from "react-i18next";

const BoldTypography = ({children}: { children: string }) => {
  return (
    <Typography variant="body1" sx={{color: "gray", fontWeight: "bold"}} gutterBottom>{children}</Typography>
  )
}

const ContentTypography = ({children}: { children: string }) => {
  return (
    <Typography sx={{color: "gray"}} gutterBottom>{children}</Typography>
  )
}

const H6 = ({children}: { children: string }) => {
  return (
    <Typography variant="h6" sx={{color: "gray"}} gutterBottom>{children}</Typography>
  )
}

const TableContent = () => {
  return (
    <table border={1}>
      <tr>
        <td style={{color: "black", textAlign: "center"}}>purpose</td>
        <td style={{color: "black", textAlign: "center"}}>types of data</td>
        <td style={{color: "black", textAlign: "center"}}>overseas recipient</td>
      </tr>
      <tr>
        <td>For maintaning and operating our Services as our data centers are in different areas (for more information
          regarding the storage arrangement, please see the “The Security and Storage of Your Information”section)
        </td>
        <td>[all types of data we collect from you as described From section IV to section VIII]</td>
        <td>We or/and our affiliates in charge of the maintainance and operation works</td>
      </tr>
    </table>
  )
}

export interface PrivacyPolicyProps {
  type?: any
}


function NGPrivacyPolicy(props: PrivacyPolicyProps) {
  const {t} = useTranslation();
  return (
    <Box sx={{width: 'calc(100% - 16px)', marginBottom: '24px'}}>
      {props.type !== 'login' && <BackGo title={t("common.Privacy Policy")}></BackGo>}
      <Box sx={{padding: "24px", backgroundColor: '#ffffff', marginTop: '-10px'}}>
        <H6>NGTECO OFFICE PRIVACY POLICY</H6>
        <H6>Last Updated: [February 10, 2026]</H6>
        <H6>1. Introduction & Scope</H6>
        <ContentTypography>This Privacy Policy describes how NGTECO (“NGTECO,” “we,” “us,” or “our”) collects, uses,
          discloses, and protects Personal Information in connection with the NGTECO Office platform, related websites,
          mobile applications, cloud services, and hardware devices (collectively, the “Services”).</ContentTypography>
        <ContentTypography>This Privacy Policy applies to users located in the United States, the European Union, the
          United Kingdom, and other jurisdictions where the Services are offered.</ContentTypography>
        <ContentTypography>NGTECO provides the Services primarily to business customers (“Customers” or “Employers”) for
          workforce time and attendance management. Employees and workers of Customers (“Employees”) use the Services
          under the direction and control of their Employer.</ContentTypography>
        <br/>
        <H6>2. Roles of the Parties (Controller vs. Processor)</H6>
        <ContentTypography>For all Employee Personal Information processed through the Services:</ContentTypography>
        <ContentTypography>- The Customer (Employer) is the Data Controller.</ContentTypography>
        <ContentTypography>- NGTECO acts solely as a Data Processor.</ContentTypography>
        <ContentTypography>NGTECO processes Employee Personal Information only on documented instructions from the
          Customer and does not determine the purposes or means of such processing.</ContentTypography>
        <ContentTypography>Customers are solely responsible for:</ContentTypography>
        <ContentTypography>- Providing legally required notices to Employees;</ContentTypography>
        <ContentTypography>- Obtaining all necessary consents or other lawful bases for processing;</ContentTypography>
        <ContentTypography>- Defining retention periods;</ContentTypography>
        <ContentTypography>- Responding to Employee privacy and data protection requests.</ContentTypography>
        <ContentTypography>Employees must direct all privacy-related requests, including requests relating to biometric
          data, to their Employer.</ContentTypography>
        <br/>
        <H6>3. Information We Collect</H6>
        <ContentTypography>3.1 Information Collected from Customers</ContentTypography>
        <ContentTypography>We may collect the following information from Customers:</ContentTypography>
        <ContentTypography>- Name, company name, and business contact information;</ContentTypography>
        <ContentTypography>- Email address and phone number;</ContentTypography>
        <ContentTypography>- Account credentials and administrative access information;</ContentTypography>
        <ContentTypography>- Device configuration, system settings, and deployment information;</ContentTypography>
        <ContentTypography>- Customer support communications and related records.</ContentTypography>
        <br/>
        <ContentTypography>3.2 Information Collected from Employees (on behalf of Customers)</ContentTypography>
        <ContentTypography>On behalf of Customers, and under their instructions, we may process:</ContentTypography>
        <ContentTypography>- Employee identifiers or IDs assigned by the Customer;</ContentTypography>
        <ContentTypography>- Work schedules and shift assignments;</ContentTypography>
        <ContentTypography>- Time and attendance records, including clock-in and clock-out events;</ContentTypography>
        <ContentTypography>- Device usage logs, such as timestamps and status information;</ContentTypography>
        <ContentTypography>- Where enabled by the Customer: biometric templates used for identity
          verification.</ContentTypography>
        <br/>
        <ContentTypography>3.3 Website & Application Information</ContentTypography>
        <ContentTypography>When users visit our websites or use our applications, we may collect:</ContentTypography>
        <ContentTypography>- IP address;</ContentTypography>
        <ContentTypography>- Browser type and operating system;</ContentTypography>
        <ContentTypography>- Referring URLs and device identifiers;</ContentTypography>
        <ContentTypography>- Usage logs, diagnostic data, and performance metrics;</ContentTypography>
        <ContentTypography>- Cookies and similar tracking technologies (as described in any applicable cookie
          notice).</ContentTypography>
        <br/>
        <H6>4. Biometric & Attendance Data</H6>
        <ContentTypography>Where Customers elect to use biometric verification features on NGTECO Devices or through the
          Services:</ContentTypography>
        <ContentTypography>- NGTECO processes biometric templates only, not raw fingerprint images or facial
          photographs.</ContentTypography>
        <ContentTypography>- Biometric templates are stored locally on the Device by default.</ContentTypography>
        <ContentTypography>- Biometric data is not uploaded to the cloud unless expressly enabled and configured by the
          Customer.</ContentTypography>
        <ContentTypography>- NGTECO does not sell, lease, trade, or commercially exploit biometric
          data.</ContentTypography>
        <ContentTypography>- NGTECO processes biometric data solely for identity verification and attendance purposes,
          as instructed by the Customer.</ContentTypography>
        <ContentTypography>Customers are solely responsible for:</ContentTypography>
        <ContentTypography>- Providing Employees with required written notices under applicable law;</ContentTypography>
        <ContentTypography>- Informing Employees of the specific purpose and length of time for which biometric data is
          collected, stored, and used;</ContentTypography>
        <ContentTypography>- Obtaining prior written consent from Employees or establishing another lawful basis where
          required by law;</ContentTypography>
        <ContentTypography>- Offering reasonable non-biometric alternatives where required by law or internal
          policy.</ContentTypography>
        <ContentTypography>Employees must direct all biometric privacy inquiries, including access, deletion, or consent
          withdrawal requests, to their Employer.</ContentTypography>
        <br/>
        <H6>5. How We Use Information</H6>
        <ContentTypography>NGTECO may use Personal Information to:</ContentTypography>
        <ContentTypography>- Provide, operate, and maintain the Services;</ContentTypography>
        <ContentTypography>- Authenticate users and manage user access rights;</ContentTypography>
        <ContentTypography>- Configure Devices and implement Customer settings;</ContentTypography>
        <ContentTypography>- Provide technical support, respond to inquiries, and resolve issues;</ContentTypography>
        <ContentTypography>- Improve product functionality, develop new features, and enhance
          security;</ContentTypography>
        <ContentTypography>- Perform analytics and performance monitoring to understand service
          usage;</ContentTypography>
        <ContentTypography>- Send administrative, billing, and service-related communications;</ContentTypography>
        <ContentTypography>- Conduct marketing communications related to NGTECO products and services, where permitted
          by law and subject to applicable opt-out mechanisms.</ContentTypography>
        <ContentTypography>NGTECO does not use Employee data for independent behavioral advertising or unrelated
          profiling.</ContentTypography>
        <ContentTypography>Customers are responsible for ensuring that any use of Employee data, including for HR or
          disciplinary purposes, complies with applicable laws and internal policies.</ContentTypography>
        <br/>
        <H6>6. Payment Processing</H6>
        <ContentTypography>All subscription and payment transactions are processed by PCI-DSS compliant third-party
          payment processors, including but not limited to Stripe, PayPal, Apple, and Google.</ContentTypography>
        <ContentTypography>NGTECO:</ContentTypography>
        <ContentTypography>- Does not store full credit card numbers;</ContentTypography>
        <ContentTypography>- Does not process card authentication data directly;</ContentTypography>
        <ContentTypography>- Receives only limited transaction metadata necessary for billing, fraud prevention, and
          support.</ContentTypography>
        <ContentTypography>Any payment information provided through third-party platforms is subject to the privacy and
          security policies of those third parties.</ContentTypography>
        <br/>
        <H6>7. How We Share Information</H6>
        <ContentTypography>NGTECO may share Personal Information only with:</ContentTypography>
        <ContentTypography>- Cloud infrastructure providers, data centers, and technical service providers acting as
          subprocessors to deliver, maintain, and secure the Services;</ContentTypography>
        <ContentTypography>- Payment processors solely for payment-related purposes;</ContentTypography>
        <ContentTypography>- Professional advisers, such as legal counsel or accountants, subject to confidentiality
          obligations.</ContentTypography>
        <ContentTypography>NGTECO does not sell Employee Personal Information or biometric data.</ContentTypography>
        <br/>
        <H6>8. Data Security</H6>
        <ContentTypography>NGTECO implements commercially reasonable administrative, technical, and physical safeguards
          designed to protect Personal Information from unauthorized access, disclosure, alteration, or
          destruction.</ContentTypography>
        <ContentTypography>These safeguards may include encryption, access controls, logging, and regular security
          assessments. However, no system can be guaranteed to be 100% secure. Customers are responsible for
          implementing appropriate internal access controls, password policies, and user management practices within
          their organizations.</ContentTypography>
        <br/>
        <H6>9. Do Not Track</H6>
        <ContentTypography>Some browsers offer a “Do Not Track” (“DNT”) setting that can signal to websites that a user
          does not want to be tracked. Because there is no universally accepted standard for DNT signals, NGTECO does
          not currently respond to DNT signals.</ContentTypography>
        <br/>
        <H6>10. Third-Party Websites</H6>
        <ContentTypography>The Services may contain links to third-party websites or services that are not operated or
          controlled by NGTECO. This Privacy Policy does not apply to such third-party websites or services. NGTECO is
          not responsible for the privacy practices or content of third parties, and we encourage users to review their
          privacy policies before providing any Personal Information.</ContentTypography>
        <br/>
        <H6>11. California Privacy Notice (CCPA / CPRA)</H6>
        <ContentTypography>This section applies solely to California residents and supplements the information contained
          in this Privacy Policy.</ContentTypography>
        <ContentTypography>11.1 Categories of Personal Information Collected</ContentTypography>
        <ContentTypography>In the past 12 months, NGTECO may have collected the following categories of Personal
          Information (as defined under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act
          (CPRA)):</ContentTypography>
        <ContentTypography>- Identifiers (e.g., name, email address, IP address);</ContentTypography>
        <ContentTypography>- Commercial information (e.g., records of Services purchased or
          considered);</ContentTypography>
        <ContentTypography>- Internet or network activity (e.g., browsing history, usage data);</ContentTypography>
        <ContentTypography>- Geolocation data (coarse location based on IP);</ContentTypography>
        <ContentTypography>- Professional or employment-related information (e.g., job title, Employer
          name);</ContentTypography>
        <ContentTypography>- Sensitive Personal Information (where applicable), such as biometric identifiers processed
          under Customer instructions.</ContentTypography>
        <br/>
        <ContentTypography>11.2 Purposes of Use</ContentTypography>
        <ContentTypography>NGTECO may use these categories of Personal Information for the following business
          purposes:</ContentTypography>
        <ContentTypography>- Operating, maintaining, and improving the Services;</ContentTypography>
        <ContentTypography>- Security and fraud prevention;</ContentTypography>
        <ContentTypography>- Customer service and support;</ContentTypography>
        <ContentTypography>- Analytics and internal research;</ContentTypography>
        <ContentTypography>- Marketing communications, where permitted by law.</ContentTypography>
        <br/>
        <ContentTypography>11.3 Rights of California Residents</ContentTypography>
        <ContentTypography>Subject to certain limitations, California residents have the right to:</ContentTypography>
        <ContentTypography>- Request to know what Personal Information NGTECO has collected, used, or
          disclosed;</ContentTypography>
        <ContentTypography>- Request access to specific pieces of Personal Information;</ContentTypography>
        <ContentTypography>- Request deletion of Personal Information;</ContentTypography>
        <ContentTypography>- Request correction of inaccurate Personal Information;</ContentTypography>
        <ContentTypography>- Opt out of the sale or sharing of Personal Information;</ContentTypography>
        <ContentTypography>- Limit the use and disclosure of Sensitive Personal Information;</ContentTypography>
        <ContentTypography>- Not receive discriminatory treatment for exercising privacy rights.</ContentTypography>
        <ContentTypography>NGTECO does not sell or share Personal Information for cross-context behavioral
          advertising.</ContentTypography>
        <br/>
        <ContentTypography>11.4 How to Exercise Rights</ContentTypography>
        <ContentTypography>Because NGTECO acts as a Data Processor for Employee data, Employees must submit requests
          relating to their Personal Information, including CCPA/CPRA rights, through their Employer (the Data
          Controller). Employers may then submit verified requests to NGTECO, and NGTECO will reasonably assist
          Customers in fulfilling such requests where required by law.</ContentTypography>
        <ContentTypography>For limited Personal Information that NGTECO processes as a Controller solely for its own
          commercial operations, such as primary Customer business contact information (including administrator name,
          business email, and billing contact), California residents may contact NGTECO directly using the contact
          information set forth below.</ContentTypography>
        <br/>
        <H6>12. EU & UK GDPR Notice</H6>
        <ContentTypography>For individuals located in the European Union or the United Kingdom, the following additional
          information applies under the EU General Data Protection Regulation (GDPR) and UK GDPR.</ContentTypography>
        <ContentTypography>12.1 Lawful Bases for Processing</ContentTypography>
        <ContentTypography>NGTECO processes Personal Data based on one or more of the following lawful
          bases:</ContentTypography>
        <ContentTypography>- Performance of a contract with the Customer (e.g., providing the Services under a
          subscription agreement);</ContentTypography>
        <ContentTypography>- Compliance with legal obligations (e.g., responding to lawful requests from
          authorities);</ContentTypography>
        <ContentTypography>- Legitimate interests in security, service delivery, fraud prevention, and product
          improvement, provided such interests are not overridden by the data subject’s rights and
          interests;</ContentTypography>
        <ContentTypography>- Consent obtained by the Customer where required by law (recognizing that in employment
          contexts, consent is typically not the sole legal basis).</ContentTypography>
        <br/>
        <ContentTypography>12.2 Data Subject Rights</ContentTypography>
        <ContentTypography>Data subjects in the EU/UK may have the right to request:</ContentTypography>
        <ContentTypography>- Access to their Personal Data;</ContentTypography>
        <ContentTypography>- Rectification of inaccurate Personal Data;</ContentTypography>
        <ContentTypography>- Erasure of Personal Data;</ContentTypography>
        <ContentTypography>- Restriction of processing;</ContentTypography>
        <ContentTypography>- Data portability;</ContentTypography>
        <ContentTypography>- Objection to certain types of processing.</ContentTypography>
        <ContentTypography>With respect to Employee Personal Data, NGTECO processes such data on behalf of Customers.
          Employees must submit data subject rights requests to their Employer as the Data Controller. NGTECO will
          reasonably assist the Employer in fulfilling such requests where required by law.</ContentTypography>
        <br/>
        <ContentTypography>12.3 International Transfers</ContentTypography>
        <ContentTypography>NGTECO may process Personal Data in countries outside the EU/UK through reputable cloud
          infrastructure providers such as Amazon Web Services (AWS). Where Personal Data is transferred outside the
          EU/UK, such transfers are safeguarded using Standard Contractual Clauses (SCCs) adopted by the European
          Commission or other lawful transfer mechanisms.</ContentTypography>
        <br/>
        <H6>13. Children’s Privacy</H6>
        <ContentTypography>The Services are not intended for individuals under the age of 16. NGTECO does not knowingly
          collect Personal Information from children. If we become aware that we have collected Personal Information
          from a child, we will take steps to delete such information in accordance with applicable
          law.</ContentTypography>
        <br/>
        <H6>14. Early Access Users (USD $9.9 Plan)</H6>
        <ContentTypography>Early Access purchasers under the historical USD $9.9 plan are governed by the terms in
          effect at the time of purchase and are not automatically enrolled into monthly or annual subscription plans.
          Any future participation in subscription-based offerings requires explicit activation by the Customer.
        </ContentTypography>
        <br/>
        <H6>15. Changes to This Privacy Policy</H6>
        <ContentTypography>NGTECO may modify this Privacy Policy from time to time to reflect changes in legal
          requirements, industry practices, or our Services. When we make material changes, we will provide notice
          through the Services or by other reasonable means. The “Last Updated” date at the top of this Privacy Policy
          indicates when it was last revised.</ContentTypography>
        <ContentTypography>Continued use of the Services after the effective date of an updated Privacy Policy
          constitutes acceptance of the revised Policy.</ContentTypography>
        <br/>
        <H6>16. Contact Information</H6>
        <ContentTypography>If you have any questions about this Privacy Policy or our privacy practices, please
          contact:</ContentTypography>
        <ContentTypography>NGTECO</ContentTypography>
        <ContentTypography>Unit B, 20/F, COS Centre, 52–56 Tsun Yip Street,</ContentTypography>
        <ContentTypography>Kwun Tong, Kowloon, Hong Kong SAR</ContentTypography>
        <ContentTypography>Support Email: ngtime@ngteco.com</ContentTypography>
        <ContentTypography>Support Phone (US): +1 770 800 2321</ContentTypography>
        <ContentTypography>Support Phone (CA): +1 438 300 8567</ContentTypography>
        <ContentTypography>Support Phone (DE): +49 322 1224 6737</ContentTypography>
        <ContentTypography>Support Phone (UK): +44 808 812 6646</ContentTypography>
        <ContentTypography>Support Phone (ES): +34 919 942 161</ContentTypography>
        <ContentTypography>Support Phone (JP): +81 800-300-8197</ContentTypography>
        <ContentTypography>Support Phone (MX): +52 800 649 0344</ContentTypography>
        <ContentTypography>Support Phone (MY): +60 3 9219 1725</ContentTypography>
      </Box>
    </Box>
  )
}

export default NGPrivacyPolicy
